import { useMemo } from 'react';
import { Platform, StyleSheet } from 'react-native';
import { palette, FONT } from '../../styles/tourTheme';
import { useMasterTourCalendarUi } from '../../contexts/FeatureFlagsContext';

export const CALENDAR_SIDEBAR_WIDTH = 380;
export const CALENDAR_SIDEBAR_WIDTH_WIDE = 760;

function v2Tokens(isDarkMode = false) {
  const pal = palette(isDarkMode);
  return {
    ink: pal.ink,
    mute: pal.mute,
    line: pal.line,
    surf: pal.surf,
    page: pal.page,
    sunk: pal.sunk,
  };
}

/** Inline card styling for member card shells. */
export function getMemberCardShellWebStyle(isDarkMode = true, v2Layout = false) {
  if (v2Layout) {
    const V2 = v2Tokens(isDarkMode);
    return {
      position: 'relative',
      overflow: 'hidden',
      borderRadius: 12,
      border: `1px solid ${V2.line}`,
      background: V2.surf,
      backgroundColor: V2.surf,
      transformStyle: 'preserve-3d',
      willChange: 'transform',
      transition: 'transform 0.2s ease-out',
      width: '100%',
    };
  }
  const glassBg = isDarkMode
    ? 'linear-gradient(145deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 42%, rgba(6,9,22,0.72) 100%)'
    : 'linear-gradient(145deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.78) 50%, rgba(245,247,250,0.92) 100%)';

  return {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 12,
    border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)'}`,
    background: glassBg,
    backgroundColor: isDarkMode ? 'rgba(6,9,22,0.58)' : 'rgba(255,255,255,0.82)',
    backdropFilter: 'blur(22px) saturate(165%)',
    WebkitBackdropFilter: 'blur(22px) saturate(165%)',
    boxShadow: isDarkMode ? '0 10px 36px rgba(0,0,0,0.38)' : '0 10px 36px rgba(0,0,0,0.08)',
    transformStyle: 'preserve-3d',
    willChange: 'transform',
    transition: 'transform 0.2s ease-out, box-shadow 0.25s ease',
    width: '100%',
  };
}

export function flattenStyleForWeb(style) {
  if (!style) return {};
  try {
    const flat = StyleSheet.flatten(style);
    if (!flat) return {};
    const web = { ...flat };
    if (flat.borderWidth != null && flat.borderColor) {
      web.border = `${flat.borderWidth}px solid ${flat.borderColor}`;
      delete web.borderWidth;
      delete web.borderColor;
    }
    if (flat.marginTop != null) web.marginTop = flat.marginTop;
    if (flat.marginBottom != null) web.marginBottom = flat.marginBottom;
    return web;
  } catch {
    return {};
  }
}

function createV2GlassStyles(isDarkMode = false) {
  const V2 = v2Tokens(isDarkMode);
  return StyleSheet.create({
    card: {
      borderWidth: 1,
      borderColor: V2.line,
      borderRadius: 12,
      padding: 12,
      backgroundColor: V2.surf,
      gap: 8,
    },
    cardInset: {
      borderWidth: 1,
      borderColor: V2.line,
      borderRadius: 12,
      padding: 10,
      backgroundColor: V2.page,
      gap: 6,
    },
    shellFrame: {
      borderWidth: 1,
      borderColor: V2.line,
      borderRadius: 12,
    },
    sectionLabel: {
      fontSize: 11,
      fontWeight: '700',
      color: V2.mute,
      textTransform: 'uppercase',
      letterSpacing: 1.1,
      fontFamily: Platform.OS === 'web' ? FONT.sans : undefined,
    },
    typePill: {
      alignSelf: 'flex-start',
      paddingHorizontal: 8,
      paddingVertical: 3,
      borderRadius: 4,
      borderWidth: 1,
    },
    typePillText: {
      fontSize: 10,
      fontWeight: '700',
    },
    statusPill: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: 4,
      borderWidth: 1,
    },
    statusText: {
      fontSize: 10,
      fontWeight: '700',
    },
    interestRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 4,
      marginTop: 2,
    },
    interestEmoji: {
      fontSize: 16,
      lineHeight: 18,
    },
  });
}

function createLegacyGlassStyles(theme, isDarkMode = true) {
  const glassBg = isDarkMode ? 'rgba(6,9,22,0.58)' : 'rgba(255,255,255,0.82)';
  const glassBorder = isDarkMode ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)';
  const webGlass =
    Platform.OS === 'web'
      ? {
          backdropFilter: 'blur(22px) saturate(165%)',
          WebkitBackdropFilter: 'blur(22px) saturate(165%)',
          boxShadow: isDarkMode ? '0 10px 36px rgba(0,0,0,0.38)' : '0 10px 36px rgba(0,0,0,0.08)',
        }
      : {};

  return StyleSheet.create({
    card: {
      borderWidth: 1,
      borderColor: glassBorder,
      borderRadius: 14,
      padding: 12,
      backgroundColor: glassBg,
      gap: 8,
      ...webGlass,
    },
    cardInset: {
      borderWidth: 1,
      borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
      borderRadius: 12,
      padding: 10,
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
      gap: 6,
    },
    shellFrame: {
      borderWidth: 1,
      borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
      borderRadius: 12,
    },
    sectionLabel: {
      fontSize: 11,
      fontWeight: '800',
      color: theme.text.secondary,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    typePill: {
      alignSelf: 'flex-start',
      paddingHorizontal: 8,
      paddingVertical: 3,
      borderRadius: 999,
      borderWidth: 1,
    },
    typePillText: {
      fontSize: 10,
      fontWeight: '700',
    },
    statusPill: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: 999,
      borderWidth: 1,
    },
    statusText: {
      fontSize: 10,
      fontWeight: '700',
    },
    interestRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 4,
      marginTop: 2,
    },
    interestEmoji: {
      fontSize: 16,
      lineHeight: 18,
    },
  });
}

/** Supporting-screen cards — v2 light cards, or original glass when the flag is off. */
export function createCalendarGlassStyles(theme, isDarkMode = true, v2Layout = false) {
  return v2Layout ? createV2GlassStyles(isDarkMode) : createLegacyGlassStyles(theme, isDarkMode);
}

export function useCalendarGlassStyles(theme, isDarkMode) {
  const v2Layout = useMasterTourCalendarUi();
  return useMemo(
    () => createCalendarGlassStyles(theme, isDarkMode, v2Layout),
    [theme, isDarkMode, v2Layout]
  );
}
